import React, { useState } from "react";
import { useIntl } from "react-intl";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import FormTitle from "./FormTitle";
import {
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  OutlinedInput,
  InputAdornment
} from "@material-ui/core";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formSection: {
      marginBottom: theme.spacing()
    },
    formFieldGrid: {
      marginBottom: theme.spacing(2)
    },
    menuPaper: {
      maxHeight: theme.spacing(48)
    },
    longQuestionLabel: {
      [theme.breakpoints.down("sm")]: {
        top: "-6px"
      }
    }
  })
);

interface IProps {
  formik: any;
}

function DetailsForm(props: IProps) {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <React.Fragment>
      <FormTitle title={intl.formatMessage({ id: "DETAILS_TITLE" })} />
      <Grid className={classes.formSection} container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="subtitle1">
            {intl.formatMessage({ id: "FRANCHISE_LOCATION_QUESTION" })}
          </Typography>
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            name="FranchiseCity"
            label={intl.formatMessage({ id: "FRANCISE_CITY_LABEL" })}
            autoComplete="name"
            value={props.formik.values.FranchiseCity}
            onBlur={props.formik.handleBlur}
            onChange={props.formik.handleChange}
            error={props.formik.touched.FranchiseCity && Boolean(props.formik.errors.FranchiseCity)}
            helperText={props.formik.touched.FranchiseCity && props.formik.errors.FranchiseCity}
          />
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            name="FranchiseDistrict"
            label={intl.formatMessage({ id: "FRANCISE_DISTRICT_LABEL" })}
            autoComplete="name"
            value={props.formik.values.FranchiseDistrict}
            onBlur={props.formik.handleBlur}
            onChange={props.formik.handleChange}
            error={props.formik.touched.FranchiseDistrict && Boolean(props.formik.errors.FranchiseDistrict)}
            helperText={props.formik.touched.FranchiseDistrict && props.formik.errors.FranchiseDistrict}
          />
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            name="FranchiseQuarter"
            label={intl.formatMessage({ id: "FRANCISE_QUARTER_LABEL" })}
            autoComplete="name"
            value={props.formik.values.FranchiseQuarter}
            onBlur={props.formik.handleBlur}
            onChange={props.formik.handleChange}
            error={props.formik.touched.FranchiseQuarter && Boolean(props.formik.errors.FranchiseQuarter)}
            helperText={props.formik.touched.FranchiseQuarter && props.formik.errors.FranchiseQuarter}
          />
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={4}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              htmlFor="outlined-adornment-amount"
              error={
                props.formik.touched.InvestmentAmount &&
                Boolean(props.formik.errors.InvestmentAmount)
              }
            >
              {intl.formatMessage({ id: "INVESTMENT_AMOUNT_QUESTION_LABEL" })}
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-amount"
              name="InvestmentAmount"
              value={props.formik.values.InvestmentAmount}
              onBlur={props.formik.handleBlur}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "");
                props.formik.handleChange(e);
              }}
              startAdornment={<InputAdornment position="start">₺</InputAdornment>}
              label={intl.formatMessage({ id: "INVESTMENT_AMOUNT_QUESTION_LABEL" })}
              inputMode="decimal"
              error={
                props.formik.touched.InvestmentAmount &&
                Boolean(props.formik.errors.InvestmentAmount)
              }
            />
            <FormHelperText
              error={
                props.formik.touched.InvestmentAmount &&
                Boolean(props.formik.errors.InvestmentAmount)
              }
            >
              {props.formik.touched.InvestmentAmount && props.formik.errors.InvestmentAmount}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={4}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              id="occupation-select-outlined-label"
              error={
                props.formik.touched.HasAnotherOccupation &&
                Boolean(props.formik.errors.HasAnotherOccupation)
              }
            >
              {intl.formatMessage({ id: "HAS_ANOTHER_OCCUPATION_QUESTION_LABEL" })}
            </InputLabel>
            <Select
              labelId="occupation-select-outlined-label"
              label={intl.formatMessage({ id: "HAS_ANOTHER_OCCUPATION_QUESTION_LABEL" })}
              name="HasAnotherOccupation"
              value={props.formik.values.HasAnotherOccupation}
              onBlur={props.formik.handleBlur}
              onChange={props.formik.handleChange}
              error={
                props.formik.touched.HasAnotherOccupation &&
                Boolean(props.formik.errors.HasAnotherOccupation)
              }
              MenuProps={{ classes: { paper: classes.menuPaper } }}
            >
              <MenuItem value={"true"}>{intl.formatMessage({ id: "YES" })}</MenuItem>
              <MenuItem value={"false"}>{intl.formatMessage({ id: "NO" })}</MenuItem>
            </Select>
            <FormHelperText
              error={
                props.formik.touched.HasAnotherOccupation &&
                Boolean(props.formik.errors.HasAnotherOccupation)
              }
            >
              {props.formik.touched.HasAnotherOccupation &&
                props.formik.errors.HasAnotherOccupation}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={4}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              htmlFor="outlined-income-amount"
              error={
                props.formik.touched.CurrentMonthlyIncome &&
                Boolean(props.formik.errors.CurrentMonthlyIncome)
              }
            >
              {intl.formatMessage({ id: "CURRENT_MONTHLY_INCOME_LABEL" })}
            </InputLabel>
            <OutlinedInput
              id="outlined-income-amount"
              name="CurrentMonthlyIncome"
              value={props.formik.values.CurrentMonthlyIncome}
              onBlur={props.formik.handleBlur}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "");
                props.formik.handleChange(e);
              }}
              startAdornment={<InputAdornment position="start">₺</InputAdornment>}
              label={intl.formatMessage({ id: "CURRENT_MONTHLY_INCOME_LABEL" })}
              inputMode="decimal"
              error={
                props.formik.touched.CurrentMonthlyIncome &&
                Boolean(props.formik.errors.CurrentMonthlyIncome)
              }
            />
            <FormHelperText
              error={
                props.formik.touched.CurrentMonthlyIncome &&
                Boolean(props.formik.errors.CurrentMonthlyIncome)
              }
            >
              {props.formik.touched.CurrentMonthlyIncome &&
                props.formik.errors.CurrentMonthlyIncome}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={12} md={6}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              htmlFor="expected-income"
              error={
                props.formik.touched.ExpectedMonthlyIncome &&
                Boolean(props.formik.errors.ExpectedMonthlyIncome)
              }
            >
              {intl.formatMessage({ id: "EXPECTED_MONTHLY_INCOME_LABEL" })}
            </InputLabel>
            <OutlinedInput
              id="expected-income"
              name="ExpectedMonthlyIncome"
              value={props.formik.values.ExpectedMonthlyIncome}
              onBlur={props.formik.handleBlur}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "");
                props.formik.handleChange(e);
              }}
              startAdornment={<InputAdornment position="start">₺</InputAdornment>}
              label={intl.formatMessage({ id: "EXPECTED_MONTHLY_INCOME_LABEL" })}
              inputMode="decimal"
              error={
                props.formik.touched.ExpectedMonthlyIncome &&
                Boolean(props.formik.errors.ExpectedMonthlyIncome)
              }
            />
            <FormHelperText
              error={
                props.formik.touched.ExpectedMonthlyIncome &&
                Boolean(props.formik.errors.ExpectedMonthlyIncome)
              }
            >
              {props.formik.touched.ExpectedMonthlyIncome &&
                props.formik.errors.ExpectedMonthlyIncome}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={12} md={6}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              id="investor-select-outlined-label"
              error={props.formik.touched.Investor && Boolean(props.formik.errors.Investor)}
            >
              {intl.formatMessage({ id: "INVESTOR_QUESTION_LABEL" })}
            </InputLabel>
            <Select
              labelId="investor-select-outlined-label"
              label={intl.formatMessage({ id: "INVESTOR_QUESTION_LABEL" })}
              name="Investor"
              value={props.formik.values.Investor}
              onBlur={props.formik.handleBlur}
              onChange={props.formik.handleChange}
              error={props.formik.touched.Investor && Boolean(props.formik.errors.Investor)}
              MenuProps={{ classes: { paper: classes.menuPaper } }}
            >
              <MenuItem value={intl.formatMessage({ id: "INVESTOR_OPTION_MYSELF" })}>
                {intl.formatMessage({ id: "INVESTOR_OPTION_MYSELF" })}
              </MenuItem>
              <MenuItem value={intl.formatMessage({ id: "INVERTOR_OPTION_PARTNER" })}>
                {intl.formatMessage({ id: "INVERTOR_OPTION_PARTNER" })}
              </MenuItem>
            </Select>
            <FormHelperText
              error={props.formik.touched.Investor && Boolean(props.formik.errors.Investor)}
            >
              {props.formik.touched.Investor && props.formik.errors.Investor}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={12} md={6}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              id="manager-select-outlined-label"
              className={classes.longQuestionLabel}
              error={props.formik.touched.Manager && Boolean(props.formik.errors.Manager)}
            >
              {intl.formatMessage({ id: "MANAGER_QUESTION_LABEL" })}
            </InputLabel>
            <Select
              labelId="manager-select-outlined-label"
              label={intl.formatMessage({ id: "MANAGER_QUESTION_LABEL" })}
              name="Manager"
              value={props.formik.values.Manager}
              onBlur={props.formik.handleBlur}
              onChange={props.formik.handleChange}
              error={props.formik.touched.Manager && Boolean(props.formik.errors.Manager)}
              MenuProps={{ classes: { paper: classes.menuPaper } }}
            >
              <MenuItem value={intl.formatMessage({ id: "MANAGER_OPTION_WILL_FIND" })}>
                {intl.formatMessage({ id: "MANAGER_OPTION_WILL_FIND" })}
              </MenuItem>
              <MenuItem value={intl.formatMessage({ id: "MANAGER_OPTION_MYSELF" })}>
                {intl.formatMessage({ id: "MANAGER_OPTION_MYSELF" })}
              </MenuItem>
              <MenuItem value={intl.formatMessage({ id: "MANAGER_OPTION_PARTNER" })}>
                {intl.formatMessage({ id: "MANAGER_OPTION_PARTNER" })}
              </MenuItem>
              <MenuItem value={intl.formatMessage({ id: "MANAGER_OPTION_WITH_PARTNER" })}>
                {intl.formatMessage({ id: "MANAGER_OPTION_WITH_PARTNER" })}
              </MenuItem>
            </Select>
            <FormHelperText
              error={props.formik.touched.Manager && Boolean(props.formik.errors.Manager)}
            >
              {props.formik.touched.Manager && props.formik.errors.Manager}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={12} md={6}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              id="experience-select-outlined-label"
              className={classes.longQuestionLabel}
              error={
                props.formik.touched.HasManagedCourierOrWarehousePersonnel &&
                Boolean(props.formik.errors.HasManagedCourierOrWarehousePersonnel)
              }
            >
              {intl.formatMessage({
                id: "HAS_MANAGED_COURIER_OR_WAREHOUSE_PERSONNEL_QUESTION_LABEL"
              })}
            </InputLabel>
            <Select
              labelId="experience-select-outlined-label"
              label={intl.formatMessage({
                id: "HAS_MANAGED_COURIER_OR_WAREHOUSE_PERSONNEL_QUESTION_LABEL"
              })}
              name="HasManagedCourierOrWarehousePersonnel"
              value={props.formik.values.HasManagedCourierOrWarehousePersonnel}
              onBlur={props.formik.handleBlur}
              onChange={props.formik.handleChange}
              error={
                props.formik.touched.HasManagedCourierOrWarehousePersonnel &&
                Boolean(props.formik.errors.HasManagedCourierOrWarehousePersonnel)
              }
              MenuProps={{ classes: { paper: classes.menuPaper } }}
            >
              <MenuItem value={"true"}>{intl.formatMessage({ id: "YES" })}</MenuItem>
              <MenuItem value={"false"}>{intl.formatMessage({ id: "NO" })}</MenuItem>
            </Select>
            <FormHelperText
              error={
                props.formik.touched.HasManagedCourierOrWarehousePersonnel &&
                Boolean(props.formik.errors.HasManagedCourierOrWarehousePersonnel)
              }
            >
              {props.formik.touched.HasManagedCourierOrWarehousePersonnel &&
                props.formik.errors.HasManagedCourierOrWarehousePersonnel}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12}>
          <TextField
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            name="WhyBanabi"
            label={intl.formatMessage({ id: "WHY_WANT_BANABI_QUESTION_LABEL" })}
            value={props.formik.values.WhyBanabi}
            onBlur={props.formik.handleBlur}
            onChange={props.formik.handleChange}
            error={props.formik.touched.WhyBanabi && Boolean(props.formik.errors.WhyBanabi)}
            helperText={props.formik.touched.WhyBanabi && props.formik.errors.WhyBanabi}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default DetailsForm;
